import { Platform } from 'react-native';

import { Layers } from '../index';

// We define a structural interface to avoid a hard dependency on 'react-native-purchases'.
// This ensures the SDK builds and runs even if the user doesn't have RevenueCat installed.
interface MinimalPurchases {
  addCustomerInfoUpdateListener: (listener: (info: MinimalCustomerInfo) => void) => void;
  getCustomerInfo: () => Promise<MinimalCustomerInfo>;
}

interface MinimalCustomerInfo {
  activeSubscriptions: string[];
  allPurchasedProductIdentifiers: string[];
  entitlements: {
    active: Record<string, any>;
  };
  latestExpirationDate?: string | null;
  originalPurchaseDate?: string;
  originalAppUserId?: string;
}

/**
 * Safe wrapper for RevenueCat integration.
 * Does not import 'react-native-purchases' to avoid runtime crashes.
 */
export const RevenueCat = {
  /**
   * Connects Layers to your existing RevenueCat Purchases instance.
   * Listens for customer info updates to track subscription status changes.
   *
   * @example
   * import Purchases from 'react-native-purchases';
   * Layers.RevenueCat.connect(Purchases);
   */
  connect: (purchasesInstance: any) => {
    if (_isConnected) {
      return;
    }

    try {
      // 1. Duck typing check: Ensure this looks like a Purchases object
      if (
        !purchasesInstance ||
        typeof purchasesInstance.addCustomerInfoUpdateListener !== 'function' ||
        typeof purchasesInstance.getCustomerInfo !== 'function'
      ) {
        console.warn(
          '[Layers] RevenueCat.connect() was passed an invalid object. Expected the default export from "react-native-purchases".'
        );
        return;
      }

      const purchases = purchasesInstance as MinimalPurchases;

      // 2. Listen for CustomerInfo updates (renewals, cancellations, new subs)
      purchases.addCustomerInfoUpdateListener((info) => {
        handleCustomerInfoUpdate(info, false);
      });

      // 3. Initial check (for state sync on app open), passing isInitial=true to suppress new events
      purchases
        .getCustomerInfo()
        .then((info) => handleCustomerInfoUpdate(info, true))
        .catch(() => {});

      _isConnected = true;
      console.log('[Layers] RevenueCat integration connected.');
    } catch (e) {
      console.warn('[Layers] Failed to connect RevenueCat:', e);
    }
  },

  /**
   * Helper to manually track a package purchase if you are not using the automatic listener
   * or want explicit purchase event timing.
   *
   * @param rcPackage - The package object from Purchases.getOfferings()
   */
  trackPurchase: async (rcPackage: any) => {
    try {
      if (!rcPackage || !rcPackage.product) return;

      const { product } = rcPackage;
      const store =
        Platform.OS === 'ios' ? 'app_store' : Platform.OS === 'android' ? 'play_store' : 'custom';

      await Layers.track('purchase_success', {
        product_id: product.identifier,
        price: product.price,
        currency: product.currencyCode,
        store,
        transaction_id: '', // RevenueCat doesn't always expose transaction ID on the package object immediately
        source: 'revenuecat'
      });
    } catch (e) {
      // safe fail
    }
  }
};

// Internal logic to process the CustomerInfo state
// We map RevenueCat state -> Layers Lifecycle Events
let _activeSubscriptionsSet = new Set<string>();
let _isInitialized = false;
let _isConnected = false;

function handleCustomerInfoUpdate(info: MinimalCustomerInfo, isInitialLoad: boolean) {
  try {
    const currentSubscriptions = new Set(info.activeSubscriptions || []);

    // If this is an initial load but we are ALREADY initialized (e.g. listener fired first),
    // do NOT process this update as it might be stale.
    if (isInitialLoad && _isInitialized) {
      return;
    }

    // Only fire events if we are NOT in the initial load
    // The initial load sets the baseline for what is "currently active"
    if (!isInitialLoad && _isInitialized) {
      // Check for NEW additions
      for (const subId of currentSubscriptions) {
        if (!_activeSubscriptionsSet.has(subId)) {
          // This is a new subscription (Start)
          Layers.track('subscription_start', {
            product_id: subId,
            platform: 'revenuecat'
          }).catch((err) => {
            console.warn('[Layers] Failed to track subscription start:', err);
          });
        }
      }
      // Note: We could also detect cancellations here by checking which items are in _activeSubscriptionsSet but NOT in currentSubscriptions
    }

    // Update state
    _activeSubscriptionsSet = currentSubscriptions;
    _isInitialized = true;

    // Sync User Properties

    const userProps: Record<string, any> = {
      is_subscriber: currentSubscriptions.size > 0
    };

    if (info.originalAppUserId) {
      userProps.revenuecat_original_app_user_id = info.originalAppUserId;
    }

    Layers.setUserProperties(userProps).catch((err) => {
      console.warn('[Layers] Failed to sync user properties:', err);
    });
  } catch (e) {
    // safe fail

    console.warn('[Layers] Error processing RevenueCat update:', e);
  }
}
